import posthog from 'posthog-js';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import type { IOnboardingTask } from '@feathr/blackbox';
import type { EOnboardingMilestone } from '@feathr/blackbox';
import { EOnboardingTaskState } from '@feathr/blackbox';
import { Button, Skeleton } from '@feathr/components';
import { useOnboardingStatus } from '@feathr/extender/hooks';
import { useFlags, useLocalUrl } from '@feathr/extender/state';
import { logUserEvents, sortBy } from '@feathr/hooks';

import MilestoneTask from '../MilestoneTask';
import type { IUseMilestoneTaskModalsReturn } from '../useMilestoneTaskModals';
import { getMilestoneTasks, type ITaskConfig, taskRequiresModal } from './MilestoneTasks.types';

interface IMilestoneTasksProps {
  tasks: IOnboardingTask[];
  milestone: EOnboardingMilestone;
  modalManagement: IUseMilestoneTaskModalsReturn;
  onTaskStatusChange?: (
    taskId: string,
    newStatus: EOnboardingTaskState.InProgress | EOnboardingTaskState.Skipped,
  ) => Promise<void>;
  isLoading?: boolean;
}

function MilestoneTasks({
  tasks,
  milestone,
  modalManagement,
  onTaskStatusChange,
  isLoading = false,
}: Readonly<IMilestoneTasksProps>): JSX.Element {
  const { t } = useTranslation();
  const localUrl = useLocalUrl();
  const history = useHistory();
  const { enforceOnboarding } = useFlags();

  const milestoneTasks = getMilestoneTasks(milestone, t);

  if (isLoading) {
    return (
      <>
        {milestoneTasks.map((task) => (
          <MilestoneTask
            description={<Skeleton paragraphs={1} width={300} />}
            key={`skeleton-${task.taskType}`}
            state={EOnboardingTaskState.NotStarted}
            title={<Skeleton width={200} />}
          >
            <Skeleton height={32} width={100} />
          </MilestoneTask>
        ))}
      </>
    );
  } else if (!tasks.length) {
    return <div>{t('No tasks found for this milestone')}</div>;
  }

  function handleTaskAction(task: IOnboardingTask, taskConfig: ITaskConfig): void {
    const taskIsActive = [
      EOnboardingTaskState.NotStarted,
      EOnboardingTaskState.InProgress,
    ].includes(task.state);

    if ([EOnboardingTaskState.Completed, EOnboardingTaskState.Skipped].includes(task.state)) {
      history.push(localUrl(taskConfig.completedActionUrl));
      return;
    }

    // Send AppCues event
    logUserEvents({
      [`onboarding_task_clicked_${task.type}`]: {
        task_type: task.type,
        task_state: task.state,
        milestone: milestone,
        task_title: taskConfig.title,
      },
    });
    posthog.capture('onboarding_task_completed', {
      task_type: task.type,
      milestone,
      account_id: sessionStorage.getItem('accountId'),
    });

    // Update task status to in progress if it's not already
    if (task.state === EOnboardingTaskState.NotStarted && onTaskStatusChange) {
      onTaskStatusChange(task.id, EOnboardingTaskState.InProgress);
    }

    // Check if this task requires a modal
    if (taskRequiresModal(task.type) && taskIsActive) {
      // Ensure only one modal is open at a time
      modalManagement.modalHandlers.closeAllModals();

      // Open the appropriate modal for this task type
      modalManagement.modalHandlers.openModalForTask(task.type);
    }

    if (!taskRequiresModal(task.type) && taskConfig.actionUrl && taskIsActive) {
      history.push(localUrl(taskConfig.actionUrl));
    }
  }

  function handleSkipTask(task: IOnboardingTask): void {
    if (task.optional && onTaskStatusChange) {
      onTaskStatusChange(task.id, EOnboardingTaskState.Skipped);
    }
  }

  function getButtonText(task: IOnboardingTask, taskConfig: ITaskConfig): string {
    switch (task.state) {
      case EOnboardingTaskState.NotStarted:
        return t('Start');

      case EOnboardingTaskState.InProgress:
        return t('Continue');

      case EOnboardingTaskState.Completed:
        return taskConfig.completedActionText;

      case EOnboardingTaskState.Skipped:
        return taskConfig.completedActionText;

      default:
        return t('Start');
    }
  }

  const sortedTasks = sortBy(
    tasks,
    'type',
    (type) => milestoneTasks.find((t: ITaskConfig) => t.taskType === type)?.order ?? 0,
  );

  // Component to handle tasks with progressive features
  function MilestoneTaskWithProgressiveFeature({
    noPermission,
    noPermissionMessage,
    task,
    taskConfig,
    onSkip,
  }: {
    noPermission: boolean;
    noPermissionMessage: string | undefined;
    task: IOnboardingTask;
    taskConfig: ITaskConfig;
    onSkip?: () => void;
  }): JSX.Element {
    const {
      isLoading: prerequisiteLoading,
      task: prerequisiteTask,
      milestone: prerequisiteMilestone,
      message: prerequisiteMessage,
    } = useOnboardingStatus(taskConfig.progressiveFeature, true);

    const isDisabled = !!(
      taskConfig.progressiveFeature &&
      (!prerequisiteTask?.isCompleted ||
        (prerequisiteMilestone && prerequisiteMilestone.state !== EOnboardingTaskState.Completed))
    );

    const tooltipMessage = isDisabled ? prerequisiteMessage : noPermissionMessage;

    return (
      <MilestoneTask
        description={taskConfig.description}
        disabled={noPermission}
        name={`milestone-task-${task.type}`}
        onSkip={onSkip}
        state={task.state}
        title={taskConfig.title}
      >
        <Button
          className={'milestone-action-button'}
          disabled={!!isDisabled || prerequisiteLoading || noPermission}
          onClick={(): void => handleTaskAction(task, taskConfig)}
          tooltip={tooltipMessage}
          type={'secondary'}
        >
          {getButtonText(task, taskConfig)}
        </Button>
      </MilestoneTask>
    );
  }

  return (
    <>
      {sortedTasks.map((task) => {
        const noPermission = !task.user_can_edit && task.state !== EOnboardingTaskState.Completed;
        const noPermissionMessage = noPermission
          ? t('You do not have permission to complete this task.')
          : undefined;

        const onSkip =
          task.optional &&
          ![EOnboardingTaskState.Completed, EOnboardingTaskState.Skipped].includes(task.state)
            ? (): void => handleSkipTask(task)
            : undefined;

        const taskConfig: ITaskConfig | undefined = milestoneTasks.find(
          (t) => t.taskType === task.type,
        );

        if (!taskConfig) {
          return null;
        }

        // Use TaskWithProgressiveFeature component if the task has a progressive feature
        if (enforceOnboarding && taskConfig.progressiveFeature) {
          return (
            <MilestoneTaskWithProgressiveFeature
              key={task.type}
              noPermission={noPermission}
              noPermissionMessage={noPermissionMessage}
              onSkip={onSkip}
              task={task}
              taskConfig={taskConfig}
            />
          );
        }

        // Default task rendering for tasks without progressive features
        return (
          <MilestoneTask
            description={taskConfig.description}
            disabled={noPermission}
            key={task.type}
            name={`milestone-task-${task.type}`}
            onSkip={onSkip}
            state={task.state}
            title={taskConfig.title}
          >
            <Button
              className={'milestone-action-button'}
              disabled={noPermission}
              onClick={(): void => handleTaskAction(task, taskConfig)}
              tooltip={noPermissionMessage}
              type={'secondary'}
            >
              {getButtonText(task, taskConfig)}
            </Button>
          </MilestoneTask>
        );
      })}
    </>
  );
}

export default MilestoneTasks;
